import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { WifiMark } from "./WifiMark";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useCountdown = (target) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const diff = new Date(target).getTime() - now;
    const safe = Number.isNaN(diff) ? 0 : Math.max(0, diff);
    return {
        days: Math.floor(safe / 86400000),
        hours: Math.floor(safe / 3600000) % 24,
        mins: Math.floor(safe / 60000) % 60,
        secs: Math.floor(safe / 1000) % 60,
        live: diff <= 0,
    };
};

const Countdown = ({ target }) => {
    const { days, hours, mins, secs, live } = useCountdown(target);
    if (live) {
        return (
            <p className="font-display font-semibold uppercase tracking-[0.2em] text-signal text-xl sm:text-2xl mb-14" data-testid="drop-countdown-live">
                The signal is live
            </p>
        );
    }
    const units = [
        ["Days", days],
        ["Hrs", hours],
        ["Min", mins],
        ["Sec", secs],
    ];
    return (
        <div className="flex justify-center gap-3 sm:gap-5 mb-14" data-testid="drop-countdown">
            {units.map(([label, value]) => (
                <div key={label} className="border border-zinc-800 px-4 sm:px-7 py-4 min-w-[72px] sm:min-w-[100px]">
                    <p className="font-display font-semibold text-3xl sm:text-5xl tabular-nums" data-testid={`countdown-${label.toLowerCase()}`}>
                        {String(value).padStart(2, "0")}
                    </p>
                    <p className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mt-2">{label}</p>
                </div>
            ))}
        </div>
    );
};

export const Drop = ({
    dropTitle = "Drop 001 — Void",
    dropLine = "10.08.2026 — Blacklist members get access 24 hours before the signal goes public.",
    dropDate = "2026-10-08",
}) => {
    const [email, setEmail] = useState("");
    const [state, setState] = useState("idle");

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setState("loading");
        try {
            await axios.post(`${API}/subscribe`, { email });
            setState("done");
        } catch {
            setState("error");
        }
    };

    return (
        <section id="drop" className="relative px-6 sm:px-12 py-32 sm:py-48 border-t border-zinc-900 overflow-hidden" data-testid="drop-section">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[70vw] max-w-[700px] max-h-[700px] rounded-full bg-[radial-gradient(circle,_rgba(255,51,51,0.08)_0%,_transparent_65%)] pointer-events-none" />

            <div className="relative max-w-3xl mx-auto text-center">
                <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: EASE }}
                >
                    <WifiMark className="h-14 sm:h-20 w-auto mx-auto mb-10" />
                </motion.div>

                <motion.h2
                    initial={{ y: 60, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: EASE, delay: 0.1 }}
                    className="font-display font-semibold uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl"
                >
                    {dropTitle}
                </motion.h2>

                <motion.p
                    initial={{ y: 40, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: EASE, delay: 0.2 }}
                    className="text-sm sm:text-base text-zinc-400 mt-6 mb-12"
                >
                    {dropLine}
                </motion.p>

                <motion.div
                    initial={{ y: 40, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: EASE, delay: 0.25 }}
                >
                    <Countdown target={dropDate} />
                </motion.div>

                <motion.div
                    initial={{ y: 40, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: EASE, delay: 0.3 }}
                >
                    {state === "done" ? (
                        <p className="font-serif italic text-2xl text-zinc-200" data-testid="subscribe-success">
                            You're on the blacklist. Stay unreachable.
                        </p>
                    ) : (
                        <form onSubmit={submit} className="flex flex-col sm:flex-row items-stretch gap-0 max-w-lg mx-auto border border-zinc-700 focus-within:border-signal transition-colors duration-500" data-testid="subscribe-form">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); setState("idle"); }}
                                placeholder="your@email.com"
                                data-testid="subscribe-email-input"
                                className="flex-1 bg-transparent px-6 py-5 text-sm tracking-wide placeholder:text-zinc-600 focus:outline-none"
                            />
                            <button
                                type="submit"
                                disabled={state === "loading"}
                                data-testid="subscribe-submit-button"
                                className="px-8 py-5 bg-white text-void text-[11px] tracking-[0.3em] uppercase font-medium hover:bg-signal hover:text-white transition-colors duration-400 disabled:opacity-50"
                            >
                                {state === "loading" ? "Joining…" : "Join the blacklist"}
                            </button>
                        </form>
                    )}
                    {state === "error" && (
                        <p className="text-signal text-xs tracking-[0.2em] uppercase mt-4" data-testid="subscribe-error">
                            Signal interference — try again
                        </p>
                    )}
                </motion.div>
            </div>
        </section>
    );
};
